import React, { Component, useState } from 'react';
import { bool, string } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Field, Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import * as validators from '../../util/validators';
import { isUploadImageOverLimitError } from '../../util/errors';
import {
  Form,
  Avatar,
  Button,
  ImageFromFile,
  IconSpinner,
  FieldTextInput,
  FieldAddress,
  FieldCustomSelect,
  NamedLink,
} from '../../components';

import css from './ProfileSettingsForm.module.css';
import { deleteProfileImage } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { findOptionsForSelectFilter } from '../../util/search';
import config from '../../config';
import { listingTypes } from '../../marketplace-custom-config';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
const industryOptions = findOptionsForSelectFilter('industry', config.custom.filters);

const ACCEPT_IMAGES = 'image/*';
const UPLOAD_CHANGE_DELAY = 2000; // Show spinner so that browser has time to load img srcset

class ProfileSettingsFormComponent extends Component {
  constructor(props) {
    super(props);

    this.uploadDelayTimeoutId = null;
    this.state = { uploadDelay: false };
    this.submittedValues = {};
  }

  componentDidUpdate(prevProps) {
    // Upload delay is additional time window where Avatar is added to the DOM,
    // but not yet visible (time to load image URL from srcset)
    if (prevProps.uploadInProgress && !this.props.uploadInProgress) {
      this.setState({ uploadDelay: true });
      this.uploadDelayTimeoutId = window.setTimeout(() => {
        this.setState({ uploadDelay: false });
      }, UPLOAD_CHANGE_DELAY);
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.uploadDelayTimeoutId);
  }

  render() {
    return (
      <FinalForm
        {...this.props}
        render={fieldRenderProps => {
          const {
            className,
            currentUser,
            handleSubmit,
            intl,
            invalid,
            onImageUpload,
            pristine,
            profileImage,
            rootClassName,
            updateInProgress,
            updateProfileError,
            uploadImageError,
            uploadInProgress,
            form,
            values,
            onUpdateProfile,
            initialValues,
            userNativeLang,
            history,
          } = fieldRenderProps;

          const user = ensureCurrentUser(currentUser);
          const textfield = 'textfield';
          const errorTextField = 'errorTextField';

          // Company name
          const companyNameLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.companyNameLabel',
          });
          const companyNamePlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.companyNamePlaceholder',
          });
          const companyNameRequiredMessage = intl.formatMessage({
            id: 'ProfileSettingsForm.companyNameRequired',
          });
          const companyNameRequired = validators.required(companyNameRequiredMessage);

          // Industry
          const industryLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.industryLabel',
          });
          const industryPlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.industryPlaceholder',
          });
          const industryRequiredMessage = intl.formatMessage({
            id: 'ProfileSettingsForm.industryRequired',
          });
          const industryRequired = validators.required(industryRequiredMessage);

          // business address
          const addressLabel = intl.formatMessage({
            id: 'SignupForm.adrressLabel',
          });
          const addressPlaceholder = intl.formatMessage({
            id: 'SignupForm.adrressPlaceholder',
          });
          const postalLabel = intl.formatMessage({
            id: 'SignupForm.postalLabel',
          });
          const postalPlaceholder = intl.formatMessage({
            id: 'SignupForm.postalPlaceholder',
          });
          const addressRequiredMessage = intl.formatMessage({
            id: 'SignupForm.adrressRequired',
          });
          const addressRequired = validators.addressRequired(addressRequiredMessage);

          // Last name
          // const lastNameLabel = intl.formatMessage({
          //   id: 'ProfileSettingsForm.lastNameLabel',
          // });
          // const lastNamePlaceholder = intl.formatMessage({
          //   id: 'ProfileSettingsForm.lastNamePlaceholder',
          // });
          // const lastNameRequiredMessage = intl.formatMessage({
          //   id: 'ProfileSettingsForm.lastNameRequired',
          // });
          // const lastNameRequired = validators.required(lastNameRequiredMessage);

          // companyDesc
          const companyDescLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.companyDescLabel',
          });
          const companyDescPlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.companyDescPlaceholder',
          });

          const uploadingOverlay =
            uploadInProgress || this.state.uploadDelay ? (
              <div className={css.uploadingImageOverlay}>
                <IconSpinner />
              </div>
            ) : null;

          const hasUploadError = !!uploadImageError && !uploadInProgress;
          const errorClasses = classNames({
            [css.avatarUploadError]: hasUploadError,
          });
          const transientUserProfileImage = profileImage.uploadedImage || user.profileImage;
          const transientUser = {
            ...user,
            profileImage: transientUserProfileImage,
          };

          // Ensure that file exists if imageFromFile is used
          const fileExists = !!profileImage.file;
          const fileUploadInProgress = uploadInProgress && fileExists;
          const delayAfterUpload = profileImage.imageId && this.state.uploadDelay;
          const imageFromFile =
            fileExists && (fileUploadInProgress || delayAfterUpload) ? (
              <ImageFromFile
                id={profileImage.id}
                className={errorClasses}
                rootClassName={css.uploadingImage}
                aspectRatioClassName={css.squareAspectRatio}
                file={profileImage.file}
              >
                {uploadingOverlay}
              </ImageFromFile>
            ) : null;

          // Avatar is rendered in hidden during the upload delay
          // Upload delay smoothes image change process:
          // responsive img has time to load srcset stuff before it is shown to user.
          const avatarClasses = classNames(errorClasses, css.avatar, {
            [css.avatarInvisible]: this.state.uploadDelay,
          });
          const avatarComponent =
            !fileUploadInProgress && profileImage.imageId ? (
              <Avatar
                className={avatarClasses}
                renderSizes="(max-width: 767px) 96px, 240px"
                user={transientUser}
                disableProfileLink
              />
            ) : null;

          const chooseAvatarLabel =
            profileImage.imageId || fileUploadInProgress ? (
              <>
                <div className={css.avatarContainer}>
                  {imageFromFile}
                  {avatarComponent}
                  {/* <div className={css.changeAvatar}>
                    <FormattedMessage id="ProfileSettingsForm.changeAvatar" />
                  </div> */}
                </div>
                {/* <button onClick={() => onUpdateProfile({ profileImageId: null })}>Remove</button> */}
              </>
            ) : (
              <div className={css.avatarPlaceholder}>
                <div className={css.avatarPlaceholderText}>
                  <FormattedMessage id="ProfileSettingsForm.addYourProfilePicture" />
                </div>
                <div className={css.avatarPlaceholderTextMobile}>
                  <FormattedMessage id="ProfileSettingsForm.addYourProfilePictureMobile" />
                </div>
              </div>
            );

          const submitError = updateProfileError ? (
            <div className={css.error}>
              <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
            </div>
          ) : null;
          const [remove, setRemove] = useState(true);
          const classes = classNames(rootClassName || css.root, className);
          const submitInProgress = updateInProgress;
          const submittedOnce = Object.keys(this.submittedValues).length > 0;
          const pristineSinceLastSubmit = submittedOnce && isEqual(values, this.submittedValues);
          const submitDisabled =
            invalid ||
            (pristine && remove) ||
            pristineSinceLastSubmit ||
            uploadInProgress ||
            submitInProgress;
          return (
            <Form
              className={classes}
              onSubmit={e => {
                this.submittedValues = values;
                handleSubmit(e);
              }}
            >
              <div className={css.sectionContainer}>
                {/* <h3 className={css.sectionTitle}>
                  <FormattedMessage id="ProfileSettingsForm.yourProfilePicture" />
                </h3> */}
                <Field
                  accept={ACCEPT_IMAGES}
                  id="profileImage"
                  name="profileImage"
                  label={chooseAvatarLabel}
                  type="file"
                  form={null}
                  uploadImageError={uploadImageError}
                  disabled={uploadInProgress}
                >
                  {fieldProps => {
                    const { accept, id, input, label, disabled, uploadImageError } = fieldProps;
                    const { name, type } = input;
                    const onChange = e => {
                      const file = e.target.files[0];
                      form.change(`profileImage`, file);

                      form.blur(`profileImage`);

                      if (file != null) {
                        const tempId = `${file.name}_${Date.now()}`;
                        onImageUpload({ id: tempId, file });
                      }
                      e.target.value = '';
                      if (!/safari/i.test(navigator.userAgent)) {
                        e.target.type = '';
                        e.target.type = 'file';
                      }
                    };

                    let error = null;

                    if (isUploadImageOverLimitError(uploadImageError)) {
                      error = (
                        <div className={css.error}>
                          <FormattedMessage id="ProfileSettingsForm.imageUploadFailedFileTooLarge" />
                        </div>
                      );
                    } else if (uploadImageError) {
                      error = (
                        <div className={css.error}>
                          <FormattedMessage id="ProfileSettingsForm.imageUploadFailed" />
                        </div>
                      );
                    }
                    return (
                      <div className={css.uploadAvatarWrapper}>
                        <label className={css.label} htmlFor={id}>
                          {label}
                        </label>
                        <div className={css.uploadContainer}>
                          {profileImage.imageId || fileUploadInProgress ? (
                            <p className={css.uploadRemoveDiv}>
                              <label className={css.uploadRemoveLabel} htmlFor={id}>
                                <FormattedMessage id="ProfileSettingsForm.changeAvatar" />
                              </label>

                              <button
                                className={css.removeButton}
                                type="button"
                                onClick={() =>
                                  // document.getElementById()
                                  onUpdateProfile({
                                    profileImageId: null,
                                  }).then(() => {
                                    setRemove(false);
                                  })
                                }
                              >
                                <FormattedMessage id="ProfileSettingsForm.removeText" />
                              </button>
                            </p>
                          ) : (
                            <p className={css.uploadRemoveDiv}>
                              <label className={css.uploadRemoveLabel} htmlFor={id}>
                                <FormattedMessage id="ProfileSettingsForm.uploadText" />
                                <span className={css.companyLogo}>
                                  <FormattedMessage id="ProfileSettingsForm.logoNameText" />
                                </span>
                              </label>
                            </p>
                          )}

                          {/* <div className={css.tip}>
                            <FormattedMessage id="ProfileSettingsForm.tip" />
                          </div>
                          <div className={css.fileInfo}>
                            <FormattedMessage id="ProfileSettingsForm.fileInfo" />
                          </div> */}
                          {error}
                        </div>
                        {/* <div className={css.uploadContainer}>
                          <span>
                            <label className={css.label} htmlFor={id}>
                              handleAdvanceSubmitupload
                            </label>
                          </span> the company logo
                        </div>

                        <div className={css.tip}>
                          <FormattedMessage id="ProfileSettingsForm.tip" />
                        </div>
                        <div className={css.fileInfo}>
                          <FormattedMessage id="ProfileSettingsForm.fileInfo" />
                        </div> */}

                        <input
                          accept={accept}
                          id={id}
                          name={name}
                          className={css.uploadAvatarInput}
                          disabled={disabled}
                          onChange={onChange}
                          type={type}
                        />
                        {/* {error} */}
                      </div>
                    );
                  }}
                </Field>
                {/* <div className={css.tip}>
                  <FormattedMessage id="ProfileSettingsForm.tip" />
                </div>
                <div className={css.fileInfo}>
                  <FormattedMessage id="ProfileSettingsForm.fileInfo" />
                </div> */}
              </div>
              <div className={classNames(css.sectionContainer, css.TextFieldContainer)}>
                {/* <h3 className={css.sectionTitle}>
                  <FormattedMessage id="ProfileSettingsForm.yourName" />
                </h3> */}
                <FieldTextInput
                  // className={css.companyName}
                  className={textfield}
                  type="text"
                  id="companyName"
                  name="companyName"
                  label={companyNameLabel}
                  placeholder={companyNamePlaceholder}
                  validate={companyNameRequired}
                />
                {/* <FieldTextInput
                    className={css.lastName}
                    type="text"
                    id="lastName"
                    name="lastName"
                    label={lastNameLabel}
                    placeholder={lastNamePlaceholder}
                    validate={lastNameRequired}
                  /> */}
                <FieldTextInput
                  // className={css.companyDescName}
                  className={textfield}
                  type="textarea"
                  id="companyDescName"
                  name="companyDescName"
                  label={companyDescLabel}
                  placeholder={companyDescPlaceholder}
                />

                <FieldCustomSelect
                  id={`industryType`}
                  name={`industryType`}
                  isSearchable={true}
                  // name="industryType"
                  userNativeLang={userNativeLang}
                  // options={industryOptions}
                  options={Object.values(listingTypes)}
                  label={industryLabel}
                  placeholder={industryPlaceholder}
                  className={classNames(css.fieldContainer, textfield)}
                  errorClassName={errorTextField}
                  validate={industryRequired}
                  // onChange={handleCateChange.bind(null, fieldName)}
                />
                {/* <FieldTextInput
                  // className={css.industryName}
                  className={textfield}
                  type="text"
                  id="industryType"
                  name="industryType"
                  label={industryLabel}
                  placeholder={industryPlaceholder}
                  validate={industryRequired}
                /> */}
                <div className={classNames(css.workerAddress, css.profileAddress)}>
                  <FieldAddress
                    id="companyAddress"
                    name="companyAddress"
                    addressLabel={addressLabel}
                    addressPlaceholder={addressPlaceholder}
                    postalLabel={postalLabel}
                    postalPlaceholder={postalPlaceholder}
                    className={textfield}
                    errorClassName={errorTextField}
                    validate={addressRequired}
                  />
                </div>
              </div>

              {/* <div className={classNames(css.sectionContainer, css.lastSection)}>
                <h3 className={css.sectionTitle}>
                  <FormattedMessage id="ProfileSettingsForm.bioHeading" />
                </h3>
                <FieldTextInput
                  type="textarea"
                  id="bio"
                  name="bio"
                  label={bioLabel}
                  placeholder={bioPlaceholder}
                />
                <p className={css.bioInfo}>
                  <FormattedMessage id="ProfileSettingsForm.bioInfo" />
                </p>
              </div> */}
              {submitError}

              <div className={css.btncomp}>
                <div className={css.submitcomp}>
                  {/* <NamedLink name="ProfilePage" params={{ id: user?.id.uuid }}> */}
                  <Button
                    className={css.submitButton}
                    type="submit"
                    inProgress={submitInProgress}
                    disabled={submitDisabled}
                    ready={pristineSinceLastSubmit}
                  >
                    <FormattedMessage id="ProfileSettingsForm.saveChanges" />
                  </Button>
                  {/* </NamedLink> */}
                </div>

                {/* <NamedLink name="ProfilePage" params={{ id: user?.id.uuid }}> */}
                <div
                  type="button"
                  onClick={() => {
                    if (profileImage?.imageId !== null && !pristine) {
                      onUpdateProfile({
                        profileImageId: null,
                      });
                    }
                    form.reset();
                    const params = { id: user?.id.uuid };
                    history.replace(
                      createResourceLocatorString('ProfilePage', routeConfiguration(), params, {})
                    );
                  }}
                >
                  <FormattedMessage id="ProfileSettingsForm.Cancel" />
                </div>
                {/* </NamedLink> */}

                {/* <span >
                  <FormattedMessage id="ProfileSettingsForm.Cancel" />
                </span> */}
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

ProfileSettingsFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  uploadImageError: null,
  updateProfileError: null,
  updateProfileReady: false,
};

ProfileSettingsFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  uploadImageError: propTypes.error,
  uploadInProgress: bool.isRequired,
  updateInProgress: bool.isRequired,
  updateProfileError: propTypes.error,
  updateProfileReady: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ProfileSettingsForm = compose(injectIntl)(ProfileSettingsFormComponent);

ProfileSettingsForm.displayName = 'ProfileSettingsForm';

export default ProfileSettingsForm;
